import { type Dispatch } from '@reduxjs/toolkit';
import { isEmpty } from 'lodash';
import { ComponentProps, useEffect, useRef, useState } from 'react';

import Popup from '@components/widgets/popup/Popup';
import Config from '@config/config';
import ErrorPopupContent from '@errors/error-popup-content/ErrorPopupContent';
import { useAppDispatch } from '@hooks/redux/useRedux';
import useGetCurrentUser from '@hooks/user/useGetCurrentUser';
import { AuthToken } from '@services/auth-token/authToken';
import { OfficeAuthToken } from '@services/auth-token/officeAuthToken';
import axios from '@services/axios/axios';
import { AuthorsAPIClient } from '@services/axios/clients/authorsAPIClient';
import { BackOfficeAPIClient } from '@services/axios/clients/backOfficeAPIClient';
import { CountriesAPIClient } from '@services/axios/clients/countriesAPIClient';
import { SocialMediaAPIClient } from '@services/axios/clients/socialMediaAPIClient';
import { setSelectedSiteStartDay } from '@services/client-config';
import { getInitialDatePickerDates } from '@services/date/dateUtils';
import { getDoesClientHasWebsite, getShouldShowAuthorFeatures, getShouldShowLanguageFeatures } from '@services/feature-validation/featureValidation';
import { OfficeLoginResponse } from '@shared/interfaces/backoffice/officeLogin';
import { logoutUser, setCurrentUser, setCurrentUserSettings } from '@store/authentication/authentication.actions';
import { setDatePickerDates } from '@store/date-picker/datePicker.actions';

const PRODUCTION_BASE_DOMAIN = 'indepth-analytics';
const LOCAL_BASE_DOMAIN      = 'indepth.local';

const withAuth = (Component: (props: ComponentProps<any>) => JSX.Element) => (props: ComponentProps<any>) => {
    const dispatch                        = useAppDispatch();
    const [error, setError]               = useState({ trigger: false, title: '', message: '' });
    const [loadComplete, setLoadComplete] = useState(false);
    const currentUser                     = useGetCurrentUser();
    const backOfficeController            = useRef<AbortController>(new AbortController());

    const shouldOverwriteUser = isEmpty(currentUser) || !currentUser?.username || !currentUser?.site;

    useEffect(() => {
        const shouldTokenBeValid = isTokenInCookies();

        if (!shouldTokenBeValid) {
            setError({
                trigger: true,
                title: 'Authentication Error',
                message: 'You\'re not logged in!',
            });

            handleLogout();

            return;
        }

        const tokenHandler     = shouldTokenBeValid;
        const [clientUsername] = window.location.hostname.split('.');

        const { allowedSites } = tokenHandler;
        const activeSite       = allowedSites?.find(site => site.slug === clientUsername);

        if (shouldOverwriteUser || clientUsername !== currentUser.site.slug) {
            tokenHandler.site = activeSite;
            dispatch(setCurrentUser(tokenHandler));
        }




        const clientName   = activeSite?.slug;
        const hasAuthors   = getShouldShowAuthorFeatures(clientName);
        const hasLanguages = getShouldShowLanguageFeatures(clientName);
        const hasWebsite   = getDoesClientHasWebsite(clientName);

        dispatch(setCurrentUserSettings({
            hasAuthors,
            hasLanguages,
            // onlySocialMedia: ['dev', 'inkyfada'].includes(tokenHandler.username),
            onlySocialMedia: hasWebsite,
        }));

        /**
         * if someone tries to replace the username with another username, logout!
         */
        if (window.location.hostname.includes(PRODUCTION_BASE_DOMAIN) || window.location.hostname.includes(LOCAL_BASE_DOMAIN)) {
            /**
             * example hostname:
             *  - username.indepth-analytics.com(this is production, on the server)
             *  - username.indepth.local(this is local environment/machine)
             */
            const [clientUsername] = window.location.hostname.split('.');

            if ( !activeSite  ) {
                setError({
                    trigger: true,
                    title: 'Authentication Error',
                    message: `The client named \`${clientUsername}\` is not logged in, redirecting to logout!`,
                });

                handleLogout();
            }
        }

        const selectedSite = activeSite;
        const startDay     = selectedSite?.config.start_day;

        setSelectedSiteStartDay(startDay ? startDay.split('T')?.[0] : startDay);
        initializeDatePicker(dispatch);
        handleBackOfficeLogin(backOfficeController).then(() => {
            setLoadComplete(true);
        }).catch(() => {
            // Even if back office login fails, allow the app to load
            setLoadComplete(true);
        });

        // watch cookies for expiration
        const cookiesWatcher = setInterval(() =>  {
            const token = AuthToken.getToken();

            if (!token) {
                handleLogout();
            }
        }, 30 * 60 * 1000); // run every 30 minutes

        return () => clearInterval(cookiesWatcher);
    }, []);

    const handleLogout = () => {
        dispatch(logoutUser());
    };

    return (
        <>
            <Popup trigger={error.trigger} shouldCloseOnFocusOut onFocusOut={() => handleLogout()}>
                <ErrorPopupContent
                    title={error.title}
                    message={error.message}
                    callback={handleLogout}
                />
            </Popup>

            {!error.trigger && loadComplete ? <Component {...props} /> : null}
        </>
    );

};

const handleBackOfficeLogin = async (backOfficeController: React.MutableRefObject<AbortController>) => {
    const token        = OfficeAuthToken.getToken();
    const refreshToken = OfficeAuthToken.getRefreshToken();

    if (token && refreshToken) {
        return null;
    }

    if (backOfficeController.current?.signal) {
        backOfficeController.current.abort();
    }

    backOfficeController.current = new AbortController();

    try {

        const result = await BackOfficeAPIClient.login(
            {
                username: Config.api.officeUsername,
                password: Config.api.officePassword,
            },
            {
                // eslint-disable-next-line
                // @ts-ignore
                signal: backOfficeController.current.signal
            }
        );

        if (!result.ok) {
            throw new Error(`Failed to authenticate! HTTP error status: ${result.status}`);
        }

        const response = (await result.json()) as OfficeLoginResponse;

        OfficeAuthToken.storeToken(
            response.token,
            response.refresh_token
        );
        BackOfficeAPIClient.initConfig();
        SocialMediaAPIClient.initConfig();

        return response;
    } catch (e) {
        console.error(e);

        return null;
    }
};

const initializeDatePicker = (dispatch: Dispatch<any>) => {
    const { startDate, endDate, compareStartDate, compareEndDate } = getInitialDatePickerDates();

    dispatch(
        setDatePickerDates(
            [startDate, endDate],
            [compareStartDate, compareEndDate],
            'HOC'
        )
    );
};

export function isTokenInCookies() {
    const token        = AuthToken.getToken();

    if (!token){
        return false;
    }

    const tokenHandler = new AuthToken(token);

    if (!tokenHandler.isValid) {
        return false;
    }

    axios.initializeConfig(tokenHandler);
    AuthorsAPIClient.initConfig(tokenHandler);
    CountriesAPIClient.initConfig(tokenHandler);

    return tokenHandler.decodedToken;
}

export default withAuth;
